import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Reservation } from "@/models/storeModel";
import { format, isSameDay, parseISO } from "date-fns";
import {
  Clock,
  Users,
  Mail,
  Phone,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { cn } from "@/lib/currencyFormatter";
import { CustomCalendarView } from "./CustomCalendarView";
import { useTranslation } from "react-i18next";
import { EditReservationDialog } from "./EditReservationDialog";
import { DeleteReservationDialog } from "./DeleteReservationDialog";
import { useStoreReservationStore } from "@/store/storeReservationStore";

interface ReservationCalendarProps {
  reservations: Reservation[];
  onReservationSelect: (reservation: Reservation) => void;
  view: "calendar" | "list";
  setView: (view: "calendar" | "list") => void;
  onCalendarCellClick?: (date: Date) => void;
}

export function ReservationCalendar({
  reservations,
  onReservationSelect,
  view,
  setView,
  onCalendarCellClick,
}: ReservationCalendarProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const { selectedReservation, setSelectedReservation, updateReservation } =
    useStoreReservationStore();
  const [editingReservation, setEditingReservation] =
    useState<Reservation | null>(selectedReservation);
  const [deletingReservationId, setDeletingReservationId] = useState<
    string | null
  >(null);

  const hasReservationsOnDate = (date: Date) => {
    return reservations.some((reservation) =>
      isSameDay(parseISO(reservation.reservation_date), date)
    );
  };

  const getReservationsForDate = (date: Date) => {
    return reservations
      .filter((reservation) =>
        isSameDay(parseISO(reservation.reservation_date), date)
      )
      .sort((a, b) => a.reservation_time.localeCompare(b.reservation_time));
  };

  const selectedDateReservations = getReservationsForDate(selectedDate);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCardBgClass = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-50";
      case "cancelled":
        return "bg-red-50";
      case "completed":
        return "bg-orange-50";
      default:
        return "bg-white";
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentMonth(date);
    if (onCalendarCellClick) {
      onCalendarCellClick(date);
    }
  };

  const handleEditClick = (e: React.MouseEvent, reservation: Reservation) => {
    e.stopPropagation();
    setSelectedReservation(reservation);
    setEditingReservation(reservation);
    setOpenEditDialog(true);
  };

  const handleContactClick = (e: React.MouseEvent, reservation: Reservation) => {
    e.stopPropagation();
    if (reservation.customer_email) {
      window.open(`mailto:${reservation.customer_email}`);
    }
  };

  const handleCompleteClick = async (e: React.MouseEvent, reservation: Reservation) => {
    e.stopPropagation();
    await updateReservation(reservation.id, { status: "completed" });
  };

  if (view === "calendar") {
    return (
      <div className="h-[calc(100vh-200px)]">
        <CustomCalendarView
          reservations={reservations}
          onReservationSelect={onReservationSelect}
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>{t("reservations.selectDate")}</CardTitle>
          </CardHeader>
          <CardContent>
            <CalendarPicker
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && handleDateSelect(date)}
              defaultMonth={currentMonth}
              className="rounded-md border pointer-events-auto"
              modifiers={{
                hasReservations: (date) => hasReservationsOnDate(date),
              }}
              modifiersStyles={{
                hasReservations: {
                  backgroundColor: "hsl(var(--primary))",
                  color: "hsl(var(--primary-foreground))",
                  fontWeight: "bold",
                },
              }}
            />
            <div className="mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <span>{t("reservations.datesWithReservations")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {t("reservations.reservationsFor", {
                  date: format(selectedDate, "EEEE, MMMM do, yyyy"),
                })}
              </span>
              <Badge variant="outline">
                {t("reservations.reservationCount", {
                  count: selectedDateReservations.length,
                })}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateReservations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("reservations.noReservations")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedDateReservations.map((reservation) => (
                  <div
                    key={reservation.id}
                    className={`rounded-lg border p-6 shadow-sm flex flex-col gap-2 relative ${getCardBgClass(reservation.status)}`}
                    onClick={() => onReservationSelect(reservation)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {reservation.customer_name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {reservation.reservation_time}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {reservation.party_size}{" "}
                            {t("reservations.guests", {
                              count: reservation.party_size,
                            })}
                          </div>
                        </div>
                      </div>
                      <Badge
                        className={cn(
                          "text-xs",
                          getStatusColor(reservation.status)
                        )}
                      >
                        {reservation.status}
                      </Badge>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{reservation.customer_email}</span>
                      </div>
                      {reservation.customer_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{reservation.customer_phone}</span>
                        </div>
                      )}
                      {reservation.special_requests && (
                        <div className="mt-3 p-2 bg-muted rounded text-sm">
                          <strong>{t("reservations.specialRequests")}:</strong>{" "}
                          {reservation.special_requests}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleEditClick(e, reservation)}
                      >
                        {t("reservations.actions.edit")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleContactClick(e, reservation)}
                      >
                        {t("reservations.actions.contact")}
                      </Button>
                      {reservation.status === "confirmed" && (
                        <Button variant="outline" size="sm" onClick={(e) => handleCompleteClick(e, reservation)}>
                          {t("reservations.actions.complete")}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <EditReservationDialog
        open={openEditDialog}
        onOpenChange={(open) => !open && setOpenEditDialog(false)}
        reservation={editingReservation!}
      />

      <DeleteReservationDialog
        open={!!deletingReservationId}
        onOpenChange={(open) => !open && setDeletingReservationId(null)}
        reservationId={deletingReservationId!}
      />
    </div>
  );
}
